import React, { useState, useRef, useEffect, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

export function PdfViewer({ data, loading }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [width, setWidth] = useState(0);
  const [err, setErr] = useState(null);
  const ref = useRef(null);

  const file = useMemo(() => (data ? { data } : null), [data]);

  useEffect(() => { setPageNumber(1); setErr(null); }, [data]);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((es) => { for (const e of es) setWidth(e.contentRect.width); });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  if (loading || !file) {
    return <div className="flex h-full items-center justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Cargando PDF…</div>;
  }

  return (
    <div className="flex h-full w-full flex-col bg-zinc-100" data-testid="pdf-viewer">
      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-b bg-white px-3 py-1.5">
        <div className="flex items-center gap-1">
          <button onClick={() => setPageNumber((p) => Math.max(1, p - 1))} disabled={pageNumber <= 1}
            className="rounded p-1.5 text-zinc-600 hover:bg-zinc-100 disabled:opacity-30" data-testid="pdf-prev"><ChevronLeft size={14} /></button>
          <span className="px-2 text-xs tabular-nums text-zinc-700">{pageNumber} / {numPages || "?"}</span>
          <button onClick={() => setPageNumber((p) => Math.min(numPages || p, p + 1))} disabled={pageNumber >= (numPages || 1)}
            className="rounded p-1.5 text-zinc-600 hover:bg-zinc-100 disabled:opacity-30" data-testid="pdf-next"><ChevronRight size={14} /></button>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setScale((s) => Math.max(0.5, s - 0.2))} className="rounded p-1.5 text-zinc-600 hover:bg-zinc-100"><ZoomOut size={14} /></button>
          <span className="w-10 text-center text-xs tabular-nums text-zinc-700">{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale((s) => Math.min(3, s + 0.2))} className="rounded p-1.5 text-zinc-600 hover:bg-zinc-100"><ZoomIn size={14} /></button>
        </div>
      </div>
      <div ref={ref} className="flex flex-1 items-start justify-center overflow-auto p-3">
        {err ? (
          <div className="p-6 text-center text-sm text-rose-700">No se pudo cargar el PDF.</div>
        ) : (
          <Document file={file} onLoadSuccess={({ numPages }) => setNumPages(numPages)} onLoadError={(e) => setErr(String(e?.message || e))}
            loading={<div className="mt-10 text-sm text-zinc-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Cargando documento…</div>}
            error={<div className="text-sm text-rose-700">Error al cargar el documento.</div>}>
            <Page pageNumber={pageNumber} width={width > 40 ? width - 24 : undefined} scale={scale}
              renderTextLayer={false} renderAnnotationLayer={false} className="shadow-lg" />
          </Document>
        )}
      </div>
    </div>
  );
}
